import { useState } from 'react';
import { api, type CutappConnection, type CutappFieldOptions, type CutappPhotoMode } from '../../services/api';
import { PhotoOverwriteWarning } from './PhotoOverwriteWarning';

export { PhotoOverwriteWarning, PHOTO_WARNING_TITLE, PHOTO_WARNING_BODY } from './PhotoOverwriteWarning';

// ─────────────────────────────────────────────────────────────────────────────
// Peca unica da sincronizacao de PRODUTOS do Cut App.
// Usada em dois lugares (Nucleo de Negocio > Produtos e Conexoes > ERPs > Cut App)
// para que as duas telas nunca divirjam: mesmos campos, mesmo aviso de fotos,
// mesma funcao de importacao.
// A credencial e a selecao/ordem de catalogos continuam so na pagina do Cut App.
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_CUTAPP_FIELD_OPTIONS: Required<CutappFieldOptions> = {
  name: true,
  description: false,
  category: false,
  dimensions: false,
  fiscal: false,
  prices: true,
};

// `sku` NAO entra aqui: e a chave de casamento (secao 11.4 do plano), sempre gravada.
// Fotos tambem nao sao um campo: sao o `photoMode` logo abaixo.
export const CUTAPP_FIELD_LABELS: { key: keyof CutappFieldOptions; title: string; desc: string }[] = [
  { key: 'name', title: 'Nome do produto', desc: 'Grava o nome do catalogo do Cut App no produto do Nucleo de Negocio.' },
  { key: 'description', title: 'Descricao', desc: 'Substitui a descricao do produto pela descricao do Cut App.' },
  { key: 'category', title: 'Categoria', desc: 'Grava a categoria do produto no Cut App. O SKU nao aparece aqui: ele e sempre a chave de casamento e nao e opcional.' },
  { key: 'dimensions', title: 'Dimensoes e peso', desc: 'Peso (kg), largura, altura e comprimento.' },
  { key: 'fiscal', title: 'Dados fiscais', desc: 'GTIN/EAN e NCM.' },
  { key: 'prices', title: 'Precos', desc: 'Permite que os precos por marketplace do Cut App alimentem a tabela de precos do Seller. Desmarcado: importa o produto sem tocar em preco.' },
];

/** Normaliza a resposta do GET /cutapp/connection (com ou sem envelope). */
export function unwrapCutappConnection(res: any): CutappConnection | null {
  if (!res) return null;
  if (res.connection != null) return res.connection as CutappConnection;
  if (res.data != null) return res.data as CutappConnection;
  if (res.baseUrl != null) return res as CutappConnection;
  return null;
}

export function unwrapCutappReport(res: any): any {
  if (!res) return null;
  return res.report ?? res.result ?? res;
}

/**
 * Importacao "agora" — unica implementacao, usada tanto pelo chip em Produtos
 * quanto pelo botao da pagina do Cut App.
 */
export async function runCutappProductImport(catalogIds: string[]): Promise<any> {
  const res = await api.runCutappImport({ catalogIds });
  return unwrapCutappReport(res);
}

// Motivos de pendencia do sync de precos (cutappSyncService.resolveLaneWrite).
export const CUTAPP_PRICE_PENDENCIA_LABELS: Record<string, string> = {
  lane_ausente: 'o Cut App nao manda preco para este marketplace',
  sem_preco: 'produto sem preco no Cut App',
  lane_sem_valor: 'o Cut App nao calculou o preco',
  base_indisponivel: 'sem o preco cheio mapeado',
  campaign_indisponivel: 'sem o preco de campanha mapeado',
  desconto_ausente: '% do relampago nao preenchido',
  desconto_abaixo_do_minimo: '% do relampago abaixo do minimo',
  min_invalido: 'preco do relampago zerado',
  min_travado_invalido: 'minimo travado sem valor',
  fura_margem_minima: 'o desconto do relampago fura a margem minima do Cut App',
  minimo_ausente: 'sem preco minimo do Cut App (defina a margem minima e rode Recalcular Custos)',
  invariante_violada: 'ordem Min <= Campanha <= Base invalida',
  produto_sem_sku: 'registro sem SKU',
};

export function cutappPendenciaText(item: any): string {
  if (typeof item === 'string') return item;
  const mp = item?.marketplace === 'shopee' ? 'Shopee' : item?.marketplace === 'mercadolivre' ? 'Mercado Livre' : null;
  return [item?.sku, mp, item?.nome || item?.name, item?.mensagem || item?.motivo || item?.reason || item?.message || CUTAPP_PRICE_PENDENCIA_LABELS[item?.tipo] || item?.tipo]
    .filter(Boolean).join(' - ');
}

/** "Shopee: 0 com preco, 42 pendencia(s) (o desconto do relampago fura a margem minima: 40)". */
export function cutappPriceSyncBreakdown(ps: any): string {
  const por = ps?.porMarketplace;
  if (!por) return '';
  return (['shopee', 'mercadolivre'] as const).map((marketplace) => {
    const info = por[marketplace];
    if (!info) return null;
    const label = marketplace === 'shopee' ? 'Shopee' : 'Mercado Livre';
    if (!info.mapeado) return `${label}: mapeamento desligado em Origem dos precos (nenhum preco puxado).`;
    const motivos = Object.entries(info.motivos || {})
      .sort((a: any, b: any) => b[1] - a[1])
      .slice(0, 2)
      .map(([tipo, count]) => `${CUTAPP_PRICE_PENDENCIA_LABELS[tipo] || tipo}: ${count}`)
      .join('; ');
    return `${label}: ${info.gravados || 0} com preco, ${info.pendencias || 0} pendencia(s)${motivos ? ` (${motivos})` : ''}.`;
  }).filter(Boolean).join(' ');
}

export function cutappReportSummary(report: any): string {
  const created = report?.created || 0;
  const updated = report?.updated || 0;
  const skipped = report?.skipped || 0;
  const pendencias = Array.isArray(report?.pendencias) ? report.pendencias.length : 0;
  // Poda: item que saiu do catalogo do Cut App perde o vinculo; sem nenhum outro
  // vinculo (marketplace incluso), o produto sai do Nucleo de Negocio junto.
  const desvinculados = Array.isArray(report?.vinculosRemovidos) ? report.vinculosRemovidos.length : 0;
  const apagados = Array.isArray(report?.produtosRemovidos) ? report.produtosRemovidos.length : 0;
  const partes = [`Cut App: ${created} criado(s), ${updated} atualizado(s), ${skipped} ignorado(s).`];
  if (desvinculados > 0) partes.push(`${desvinculados} desvinculado(s) do Cut App.`);
  if (apagados > 0) partes.push(`${apagados} produto(s) apagado(s) por nao existir em nenhuma plataforma.`);
  if (pendencias > 0) partes.push(`${pendencias} pendencia(s).`);
  // Precos do Seller: o import encadeia o sync de precos quando "Precos" esta marcado.
  const ps = report?.priceSync;
  if (ps) {
    if (ps.skipped === true || ps.success === false) {
      partes.push(`Precos do Seller nao atualizados: ${ps.message || ps.reason || ps.error}`);
    } else {
      const psPend = Array.isArray(ps.pendencias) ? ps.pendencias.length : 0;
      partes.push(`Precos do Seller: ${ps.created || 0} criado(s), ${ps.updated || 0} atualizado(s), ${ps.unchanged || 0} sem mudanca${psPend > 0 ? `, ${psPend} pendencia(s)` : ''}.`);
      const porMarketplace = cutappPriceSyncBreakdown(ps);
      if (porMarketplace) partes.push(porMarketplace);
    }
  }
  return partes.join(' ');
}

// ─── Modal de configuracao (irmao do ProductSyncConfigModal dos marketplaces) ──

export function CutappProductSyncModal({
  initialFields,
  initialPhotoMode,
  saving,
  saveLabel,
  subtitle,
  onClose,
  onSave,
}: {
  initialFields?: CutappFieldOptions | null;
  initialPhotoMode?: CutappPhotoMode | null;
  saving?: boolean;
  saveLabel?: string;
  subtitle?: string;
  onClose: () => void;
  onSave: (fields: Required<CutappFieldOptions>, photoMode: CutappPhotoMode) => void;
}) {
  const [fields, setFields] = useState<Required<CutappFieldOptions>>({ ...DEFAULT_CUTAPP_FIELD_OPTIONS, ...(initialFields || {}) });
  const [photoMode, setPhotoMode] = useState<CutappPhotoMode>(initialPhotoMode === 'overwrite' ? 'overwrite' : 'skip');
  const [photoConfirmOpen, setPhotoConfirmOpen] = useState(false);

  const toggle = (key: keyof CutappFieldOptions) => setFields(prev => ({ ...prev, [key]: !prev[key] }));

  // O aviso reaparece SEMPRE que a opcao for alterada para sobrescrever, nao so
  // na primeira vez (secao 11.3 do plano).
  const handlePhotoModeChange = (mode: CutappPhotoMode) => {
    if (mode === 'overwrite') { setPhotoConfirmOpen(true); return; }
    setPhotoMode('skip');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
        <p className="text-xs font-bold uppercase tracking-widest text-purple-600">Sincronizacao de produtos</p>
        <h3 className="text-lg font-black text-slate-900">O que o Cut App pode gravar no produto</h3>
        <p className="mb-4 mt-1 text-xs text-slate-500">
          {subtitle || 'Cada campo marcado passa a ser de posse desta conexao: os syncs de Shopee/Mercado Livre nao escrevem nele. Campos desmarcados ficam intactos. O casamento e sempre por SKU.'}
        </p>

        <div className="space-y-2">
          {CUTAPP_FIELD_LABELS.map(field => (
            <label key={field.key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-white">
              <input type="checkbox" checked={!!fields[field.key]} onChange={() => toggle(field.key)} className="mt-0.5 accent-purple-600" />
              <span>
                <span className="block text-sm font-semibold text-slate-800">{field.title}</span>
                <span className="block text-xs text-slate-500">{field.desc}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="mt-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Fotos do produto</p>
          <p className="mt-1 text-xs text-slate-500">Escolha explicita: o padrao e <strong>nao trazer fotos</strong>. Sobrescrever e destrutivo.</p>
          <div className="mt-2 space-y-2">
            <label className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 ${photoMode === 'skip' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}>
              <input type="radio" name="cutapp-photo-mode-modal" checked={photoMode === 'skip'} onChange={() => handlePhotoModeChange('skip')} className="mt-0.5 accent-emerald-600" />
              <span>
                <span className="block text-sm font-semibold text-slate-800">Nao trazer fotos (padrao)</span>
                <span className="block text-xs text-slate-500">As fotos atuais do produto e as geradas nos pipelines ficam intactas.</span>
              </span>
            </label>
            <label className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 ${photoMode === 'overwrite' ? 'border-red-500 bg-red-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}>
              <input type="radio" name="cutapp-photo-mode-modal" checked={photoMode === 'overwrite'} onChange={() => handlePhotoModeChange('overwrite')} className="mt-0.5 accent-red-600" />
              <span>
                <span className="block text-sm font-semibold text-red-700">Sobrescrever com as fotos do Cut App</span>
                <span className="block text-xs text-red-600">Remove as fotos existentes. Leia o aviso antes de confirmar.</span>
              </span>
            </label>
          </div>
          {photoMode === 'overwrite' && (
            <div className="mt-3">
              <PhotoOverwriteWarning compact />
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button
            onClick={() => onSave(fields, photoMode)}
            disabled={!!saving}
            className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : (saveLabel || 'Habilitar auto 24h')}
          </button>
        </div>
      </div>

      {photoConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-base font-bold text-red-700">Confirmar sobrescrita das fotos</h2>
              <p className="text-sm text-slate-500">Leia antes de confirmar. Esta acao nao tem volta.</p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              <PhotoOverwriteWarning />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setPhotoConfirmOpen(false)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancelar (manter sem trazer fotos)
              </button>
              <button
                type="button"
                onClick={() => { setPhotoMode('overwrite'); setPhotoConfirmOpen(false); }}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Entendi, sobrescrever as fotos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
